import { useRef, useCallback } from 'react'

/**
 * Creates a debounced callback that delays execution until after wait time has passed
 * since the last time it was invoked.
 * 
 * @param callback - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the callback
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  wait: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args)
        timeoutRef.current = null
      }, wait)
    },
    [callback, wait]
  ) as T

  return debouncedCallback
}

